<!-- HERO -->
<section class="hero">
  <div class="container">
    <div class="hero-grid">
      <!-- Left: Copy -->
      <div class="hero-copy">
        <span class="hero-badge">
          <span class="badge-dot"></span>
          Trusted by 5,000+ SEO professionals
        </span>
        <h1 class="hero-title">
          Index URLs in <span class="text-gradient">minutes</span>, not weeks.
        </h1>
        <p class="hero-subtitle">
          Submit URLs and trigger instant discovery by Google, Bing, and 200+ search nodes. Built for SEO agencies, affiliate marketers, and developers who need results fast.
        </p>
        <div class="hero-actions">
          <button class="btn btn-primary btn-xl" (click)="getStarted()">
            {{ isLoggedIn ? 'Open dashboard' : 'Start indexing free' }}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </button>
          <a routerLink="/api-docs" class="btn btn-outline btn-xl">
            View API docs
          </a>
        </div>
        <div class="hero-meta">
          <div class="meta-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            No credit card required
          </div>
          <div class="meta-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            98.5% success rate
          </div>
          <div class="meta-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            Credits never expire
          </div>
        </div>
      </div>

      <!-- Right: Terminal demo -->
      <div class="hero-demo">
        <div class="terminal">
          <div class="terminal-header">
            <div class="terminal-dots">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
            <span class="terminal-title">indexer.sh</span>
          </div>
          <div class="terminal-body">
            <div class="terminal-line">
              <span class="terminal-prompt">$</span>
              <span class="terminal-text">quickindex submit</span>
            </div>
            <div class="terminal-input">
              <span class="terminal-prompt">→</span>
              <input
                type="text"
                class="terminal-input-field"
                placeholder="https://example.com/your-page"
                [(ngModel)]="demoUrl"
                (keyup.enter)="runDemo()"
              />
            </div>
            <button class="terminal-btn" (click)="runDemo()">
              <span>Run</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            </button>
            <div class="terminal-output" *ngIf="demoOutput">
              <pre>{{ demoOutput }}</pre>
            </div>
            <div class="terminal-stats" *ngIf="!demoOutput">
              <div class="terminal-stat">
                <span class="stat-key">avg_response</span>
                <span class="stat-val">87ms</span>
              </div>
              <div class="terminal-stat">
                <span class="stat-key">success_rate</span>
                <span class="stat-val">98.5%</span>
              </div>
              <div class="terminal-stat">
                <span class="stat-key">uptime</span>
                <span class="stat-val">99.9%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- STATS STRIP -->
<section class="stats-strip">
  <div class="container">
    <div class="stats-grid">
      <div class="stat-block" *ngFor="let stat of stats">
        <div class="stat-value mono">{{ formatStatValue(stat) }}</div>
        <div class="stat-label">{{ stat.label }}</div>
      </div>
    </div>
  </div>
</section>

<!-- PLATFORMS -->
<section class="platforms-strip">
  <div class="container">
    <p class="platforms-label">Works with every major platform and CMS</p>
    <div class="platforms-grid">
      <span class="platform" *ngFor="let platform of platforms">{{ platform }}</span>
    </div>
  </div>
</section>

<!-- HOW IT WORKS -->
<section class="how-section" id="how">
  <div class="container">
    <div class="section-header">
      <span class="section-tag">How it works</span>
      <h2 class="section-title">Three steps to indexed pages</h2>
      <p class="section-subtitle">From URL submission to search engine discovery in under 2 minutes.</p>
    </div>

    <div class="steps-grid">
      <div class="step-card reveal" *ngFor="let step of steps; let i = index">
        <div class="step-number mono">{{ step.number }}</div>
        <div class="step-icon">
          <svg *ngIf="step.icon === 'upload'" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          <svg *ngIf="step.icon === 'radar'" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/><line x1="12" y1="2" x2="12" y2="6"/></svg>
          <svg *ngIf="step.icon === 'chart'" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
        </div>
        <h3 class="step-title">{{ step.title }}</h3>
        <p class="step-desc">{{ step.description }}</p>
        <div class="step-connector" *ngIf="i < steps.length - 1"></div>
      </div>
    </div>
  </div>
</section>

<!-- FEATURES -->
<section class="features-section" id="features">
  <div class="container">
    <div class="section-header">
      <span class="section-tag">Features</span>
      <h2 class="section-title">Everything you need to index at scale</h2>
      <p class="section-subtitle">Powerful tools built for technical SEOs and developers.</p>
    </div>

    <div class="features-grid">
      <div class="feature-card reveal" *ngFor="let feature of features">
        <div class="feature-icon">
          <svg *ngIf="feature.icon === 'zap'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
          <svg *ngIf="feature.icon === 'shield'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          <svg *ngIf="feature.icon === 'code'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
          <svg *ngIf="feature.icon === 'clock'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          <svg *ngIf="feature.icon === 'database'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>
          <svg *ngIf="feature.icon === 'activity'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
          <svg *ngIf="feature.icon === 'credit'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
          <svg *ngIf="feature.icon === 'lock'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        </div>
        <h3 class="feature-title">{{ feature.title }}</h3>
        <p class="feature-desc">{{ feature.description }}</p>
      </div>
    </div>
  </div>
</section>

<!-- TESTIMONIALS -->
<section class="testimonials-section">
  <div class="container">
    <div class="section-header">
      <span class="section-tag">Customers</span>
      <h2 class="section-title">Trusted by SEO professionals</h2>
    </div>
    <div class="testimonials-grid">
      <div class="testimonial-card reveal" *ngFor="let t of testimonials">
        <svg class="quote-icon" width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M14 17h3l2-4V7h-6v6h3M6 17h3l2-4V7H5v6h3l-2 4z"/></svg>
        <p class="testimonial-text">{{ t.text }}</p>
        <div class="testimonial-author">
          <div class="author-avatar">{{ t.name.charAt(0) }}</div>
          <div>
            <div class="author-name">{{ t.name }}</div>
            <div class="author-role">{{ t.role }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- PRICING -->
<section class="pricing-section" id="pricing">
  <div class="container">
    <div class="section-header">
      <span class="section-tag">Pricing</span>
      <h2 class="section-title">Simple, transparent pricing</h2>
      <p class="section-subtitle">Pay per URL. No subscriptions, no hidden fees. Credits never expire.</p>
    </div>

    <div class="pricing-grid">
      <div class="pricing-card reveal"
           *ngFor="let pkg of packages; let i = index"
           [class.popular]="pkg.badge === 'Most Popular'">
        <div class="pricing-badge" *ngIf="pkg.badge">{{ pkg.badge }}</div>
        <h3 class="pricing-name">{{ pkg.name }}</h3>
        <div class="pricing-credits">{{ pkg.credits.toLocaleString() }} <span>credits</span></div>
        <div class="pricing-price">
          <span class="price-currency">$</span>
          <span class="price-amount">{{ pkg.price }}</span>
        </div>
        <div class="pricing-meta">
          <span class="price-original">${{ pkg.original_price }}</span>
          <span class="price-save mono">Save {{ getDiscount(pkg) }}%</span>
        </div>
        <div class="price-per mono">${{ getPerCredit(pkg) }} per credit</div>

        <ul class="pricing-features">
          <li>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            {{ pkg.credits }} URL submissions
          </li>
          <li>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            Auto-deduplication
          </li>
          <li>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            Developer API access
          </li>
          <li>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            Real-time tracking
          </li>
          <li>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            Credits never expire
          </li>
        </ul>

        <a routerLink="/pricing" class="btn" [class.btn-primary]="pkg.badge === 'Most Popular'" [class.btn-secondary]="pkg.badge !== 'Most Popular'">
          Get started
        </a>
      </div>
    </div>

    <div class="pricing-footer">
      <p>Need more? <a routerLink="/contact-us">Contact us</a> for custom volume pricing.</p>
    </div>
  </div>
</section>

<!-- FAQ -->
<section class="faq-section">
  <div class="container container-sm">
    <div class="section-header">
      <span class="section-tag">FAQ</span>
      <h2 class="section-title">Frequently asked questions</h2>
    </div>

    <div class="faq-list">
      <div class="faq-item"
           *ngFor="let faq of homeFaqs"
           [class.open]="faq.open">
        <button class="faq-trigger" (click)="toggleHomeFaq(faq)">
          <span>{{ faq.question }}</span>
          <svg class="faq-chevron" [class.rotated]="faq.open" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </button>
        <div class="faq-content" *ngIf="faq.open">
          <p>{{ faq.answer }}</p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- FINAL CTA -->
<section class="cta-section">
  <div class="container">
    <div class="cta-card reveal">
      <h2 class="cta-title">Ready to index smarter?</h2>
      <p class="cta-subtitle">Join 5,000+ SEO professionals who trust Quick Indexing for their indexing pipeline.</p>
      <div class="cta-actions">
        <button class="btn btn-primary btn-xl" (click)="getStarted()">
          {{ isLoggedIn ? 'Open dashboard' : 'Start indexing free' }}
        </button>
        <a routerLink="/api-docs" class="btn btn-outline btn-xl">Read the docs</a>
      </div>
    </div>
  </div>
</section>
